import { Injectable, linkedSignal, signal } from '@angular/core';
import type { DocumentUploadValue } from '@sportello-balneari/shared-components';

/** One uploaded file as the answer API returns it. */
export interface AnswerAttachment {
  category: string;
  envelope: string;
  fileName: string;
  id: string;
  tenderFormAnswerId: string;
  tenderFormAnswerName: string;
  downloadUrl: string;
  blobName: string;
  indexingStatus?: string | null;
  indexingError?: string | null;
}

/**
 * The answer's uploaded files, plus one writable view per envelope.
 *
 * The per-envelope views are `linkedSignal`, not `computed`: they re-derive
 * when the flat list is replaced on load, but the upload components also write
 * to them directly as files are added and removed.
 */
@Injectable()
export class AnswerAttachmentsService {
  readonly tenderFormAnswerAttachments = signal<AnswerAttachment[]>([]);

  readonly envelopeAAttachments = this.forEnvelope('ENVELOPE_A');
  readonly envelopeBAttachments = this.forEnvelope('ENVELOPE_B');
  readonly envelopeCAttachments = this.forEnvelope('ENVELOPE_C');

  /** A writable, category-keyed view of one envelope's files. */
  private forEnvelope(envelope: string) {
    return linkedSignal(() =>
      this.tenderFormAnswerAttachments()
        .filter((a) => a.envelope === envelope)
        .reduce((acc, attachment) => {
          const category = attachment.category || 'uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push({
            id: attachment.id,
            fileName: attachment.fileName,
            url: attachment.downloadUrl,
            indexingStatus: attachment.indexingStatus,
            indexingError: attachment.indexingError,
            metadata: {
              blobName: attachment.blobName,
              envelope: attachment.envelope,
              category: attachment.category,
            },
          });
          return acc;
        }, {} as DocumentUploadValue),
    );
  }
}
